import { useCallback, useEffect, useRef, useState } from 'react'
import { ArrowDown, ArrowUp, ArrowUpDown, Pencil } from 'lucide-react'
import { showSuccess, showApiError } from '@/lib/utils/notifications'
import applicationStyles from '@/features/application/styles/applicationList.module.css'
import { PageShell } from '@/components/layout/PageShell'
import { ListingTableLoadingRow } from '@/components/ui/ModuleScreenLoading'
import { RoleEditModal } from '../components/RoleEditModal'
import { getAllPolicy, getAllTiles, getRoles, saveRole } from '../services/rolesService'
import type {
  PolicyByCategory,
  Role,
  RoleListParams,
  RoleSortKey,
  RoleSubmitPayload,
  SortDirection,
  TileSummary,
} from '../types'

const DEFAULT_PARAMS: RoleListParams = {
  orderBy: 'role_name',
  sortBy: 'asc',
  perPage: 50,
}

/** Angular `user-roles-list`: `{{ userRole?.role_description?.substring(0, 40) }}` (plain text, escaped). */
function roleDescriptionListCell(raw: string | undefined): string {
  const s = raw ?? ''
  return s.length > 40 ? s.slice(0, 40) : s
}

export function RolesListPage() {
  const [roles, setRoles] = useState<Role[]>([])
  const [params, setParams] = useState<RoleListParams>(DEFAULT_PARAMS)
  const [loading, setLoading] = useState(false)
  const [editOpen, setEditOpen] = useState(false)
  const [editRoleKey, setEditRoleKey] = useState<string | null>(null)
  const [submitting, setSubmitting] = useState(false)
  const [tileCatalog, setTileCatalog] = useState<TileSummary[]>([])
  const [policyCatalog, setPolicyCatalog] = useState<PolicyByCategory>({})
  const mountedRef = useRef(false)

  const load = useCallback(async (next: RoleListParams) => {
    setLoading(true)
    try {
      setRoles(await getRoles(next))
    } catch (error) {
      showApiError(error)
    } finally {
      setLoading(false)
    }
  }, [])

  useEffect(() => {
    if (mountedRef.current) return
    mountedRef.current = true
    void load(DEFAULT_PARAMS)
  }, [load])

  /** Preload tile + policy catalogs so the edit modal only calls GET /api/user_roles/:key. */
  useEffect(() => {
    let cancelled = false
    void Promise.all([getAllTiles(), getAllPolicy()])
      .then(([tiles, policy]) => {
        if (!cancelled) {
          setTileCatalog(tiles)
          setPolicyCatalog(policy)
        }
      })
      .catch((error) => {
        if (!cancelled) showApiError(error)
      })
    return () => {
      cancelled = true
    }
  }, [])

  const applyParams = useCallback(
    (next: RoleListParams) => {
      setParams(next)
      void load(next)
    },
    [load],
  )

  /** Parity with Angular `UserRolesListComponent.sort()`: every header click flips
   *  asc/desc, then sets `orderBy` to that column. */
  const toggleSort = (key: RoleSortKey) => {
    const nextDir: SortDirection = params.sortBy === 'asc' ? 'desc' : 'asc'
    applyParams({ ...params, orderBy: key, sortBy: nextDir })
  }

  const openEdit = (role: Role) => {
    if (!role.role_key) return
    setEditRoleKey(role.role_key)
    setEditOpen(true)
  }

  const handleSubmit = async (roleKey: string, payload: RoleSubmitPayload) => {
    setSubmitting(true)
    try {
      await saveRole(roleKey, payload)
      showSuccess('Data Updated Successfully')
      setEditOpen(false)
      setEditRoleKey(null)
      void load(params)
    } catch (error) {
      showApiError(error)
    } finally {
      setSubmitting(false)
    }
  }

  const SortArrows = ({ column }: { column: RoleSortKey }) => {
    if (params.orderBy === column) {
      return params.sortBy === 'asc' ? (
        <ArrowUp className="inline h-3.5 w-3.5 shrink-0 text-slate-500" aria-hidden />
      ) : (
        <ArrowDown className="inline h-3.5 w-3.5 shrink-0 text-slate-500" aria-hidden />
      )
    }
    return <ArrowUpDown className="inline h-3.5 w-3.5 shrink-0 text-slate-300" aria-hidden />
  }

  return (
    <PageShell title="ROLES">
      <div className="flex min-w-0 flex-col overflow-hidden rounded-xl border border-slate-200 bg-white shadow-sm mt-4">
        <div className="overflow-x-auto">
          <table className={applicationStyles.listingTable}>
            <thead>
            <tr>
              <th aria-sort={params.orderBy === 'role_name' ? (params.sortBy === 'asc' ? 'ascending' : 'descending') : 'none'}>
                <button
                  type="button"
                  className={applicationStyles.sortHeaderButton}
                  onClick={() => toggleSort('role_name')}
                >
                  <span className={applicationStyles.sortHeaderLabel}>Role</span>
                  <SortArrows column="role_name" />
                </button>
              </th>
              <th aria-sort={params.orderBy === 'role_description' ? (params.sortBy === 'asc' ? 'ascending' : 'descending') : 'none'}>
                <button
                  type="button"
                  className={applicationStyles.sortHeaderButton}
                  onClick={() => toggleSort('role_description')}
                >
                  <span className={applicationStyles.sortHeaderLabel}>Description</span>
                  <SortArrows column="role_description" />
                </button>
              </th>
              <th aria-sort={params.orderBy === 'report_url' ? (params.sortBy === 'asc' ? 'ascending' : 'descending') : 'none'}>
                <button
                  type="button"
                  className={applicationStyles.sortHeaderButton}
                  onClick={() => toggleSort('report_url')}
                >
                  <span className={applicationStyles.sortHeaderLabel}>Report URL</span>
                  <SortArrows column="report_url" />
                </button>
              </th>
              <th>Action</th>
            </tr>
          </thead>
          <tbody>
            {loading ? (
              <ListingTableLoadingRow colSpan={4} />
            ) : roles.length === 0 ? (
              <tr>
                <td colSpan={4} className="px-4 py-8 text-center text-slate-500">
                  No roles found.
                </td>
              </tr>
            ) : (
              roles.map((role) => (
                <tr key={role.role_key ?? role.role_name}>
                  <td className="font-medium text-slate-900">{role.role_name}</td>
                  <td className="text-slate-700 max-w-xl align-top">
                    {roleDescriptionListCell(role.role_description)}
                  </td>
                  <td className="text-slate-600 text-xs break-all">
                    {role.report_url ? (
                      <a
                        href={role.report_url}
                        target="_blank"
                        rel="noopener noreferrer"
                        className="font-mono text-blue-600 hover:underline"
                      >
                        {role.report_url}
                      </a>
                    ) : (
                      '—'
                    )}
                  </td>
                  <td>
                    <button
                      type="button"
                      onClick={() => openEdit(role)}
                      className="p-1.5 rounded hover:bg-blue-50 text-blue-600"
                      aria-label="Edit"
                      title="Edit"
                    >
                      <Pencil className="h-4 w-4" />
                    </button>
                  </td>
                </tr>
              ))
            )}
          </tbody>
        </table>
        </div>
      </div>

      <RoleEditModal
        isOpen={editOpen}
        roleKey={editRoleKey}
        allRoles={roles}
        catalogTiles={tileCatalog}
        catalogPolicyByCategory={policyCatalog}
        submitting={submitting}
        onClose={() => {
          setEditOpen(false)
          setEditRoleKey(null)
        }}
        onSubmit={handleSubmit}
      />
    </PageShell>
  )
}
